import { EmergencyTeam } from './EmergencyTeam';
import { PatientDetailInfo } from './PatientDetailInfo';
import { JianInformation } from './JianInformation';
import { useSelectedPatient } from './hooks/useSelectedPatient';
import { ErrorMessage } from '../common/ErrorMessage';
import {
  IncidentDetail,
  IncidentDetailEmsTeam,
  IncidentDetailPatients,
  useIncidentDetailForNiigata,
} from 'sections/niigata/features/IncidentDetail/useIncidentDetailForNiigata';

type JianDetailBaseProps = {
  /** 事案情報。 */
  incidentInfo: IncidentDetail;
  /** 救急隊編成。 */
  emsTeam: IncidentDetailEmsTeam;
  /** 傷病者情報。 */
  patientInfo: IncidentDetailPatients;
};

const JianDetailBase = ({
  incidentInfo,
  emsTeam,
  patientInfo,
}: JianDetailBaseProps) => {
  const { selectedPatient, patientNameList, selectPatient } =
    useSelectedPatient({ patientInfo });

  return (
    <>
      <IncidentInfoWrapper>
        <JianInformation
          incidentInfo={incidentInfo}
          timeSeries={selectedPatient.timeSeries}
        />
        <EmergencyTeam emsTeam={emsTeam} />
      </IncidentInfoWrapper>
      <PatientDetailInfo
        patientInfo={selectedPatient}
        selectPatient={selectPatient}
        patientNameList={patientNameList}
        destinationHospitalId={incidentInfo.hospitalId}
      />
    </>
  );
};

type NoPatientInfoProps = {
  /** 事案情報。 */
  incidentInfo: IncidentDetail;
  /** 救急隊編成。 */
  emsTeam: IncidentDetailEmsTeam;
};

const NoPatientInfo = ({ incidentInfo, emsTeam }: NoPatientInfoProps) => {
  return (
    <IncidentInfoWrapper>
      <JianInformation incidentInfo={incidentInfo} />
      <EmergencyTeam emsTeam={emsTeam} />
    </IncidentInfoWrapper>
  );
};

/** 事案詳細のコンポーネント。 */
export const JianDetail = () => {
  const { incidentDetailData, isError } = useIncidentDetailForNiigata();

  // エラーが発生した場合の表示
  if (isError) {
    return (
      <CenteredContainer>
        <ErrorMessage />
      </CenteredContainer>
    );
  }

  // データが存在しない場合の表示
  if (!incidentDetailData) {
    return (
      <CenteredContainer>
        <p className="text-[16px] noto-sans-jp text-textPrimary">
          事案詳細が見つかりませんでした。
        </p>
      </CenteredContainer>
    );
  }

  const { incidentInfo, emsTeamInfo, patientInfo } = incidentDetailData;

  return (
    <div className="flex flex-col m-auto">
      {patientInfo.length > 0 ? (
        <JianDetailBase
          incidentInfo={incidentInfo}
          emsTeam={emsTeamInfo}
          patientInfo={patientInfo}
        />
      ) : (
        <NoPatientInfo incidentInfo={incidentInfo} emsTeam={emsTeamInfo} />
      )}
    </div>
  );
};

export const IncidentInfoWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div className="bg-surface">
      <CenteredContainer>{children}</CenteredContainer>
    </div>
  );
};

const CenteredContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex flex-col gap-[24px] max-w-[1280px] pt-[40px] px-[40px] m-auto">
      {children}
    </div>
  );
};
